﻿import React, { useState, useEffect, useRef } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { Decimal4Format } from '../templates/pw-formatting';
import { CalculatedComponentActual } from '../models/calculated-component-actual';
import { DataTemplates } from '../templates/data-templates';
import { PWDates } from '../utilities/pw-dates';
import { Asset } from '../models/asset';
import { EditType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import RawMaterialSearch from './RawMaterialSearch';
import { RawMaterial } from '../models/raw-material';
import { RawMaterialBatch } from '../models/raw-material-batch';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Toolbar,
    Summary,
    TotalItem,
    Lookup,
    Button as ColumnButton,
    Item as ToolbarItem,
    LoadPanel,
    Editing
} from 'devextreme-react/data-grid';

import SelectBox, { SelectBoxTypes } from 'devextreme-react/select-box';
import Guid from 'devextreme/core/guid';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 1;

export interface Props {
    batchId: string;
    balances?: Asset[];
    isReadOnly: boolean;
    onClose?: () => void;
}

export default function ProductionBenchSheetSolventsComponent(props: Props) {
    let datagridRef: any = useRef(null);

    const [solvents, setSolvents] =
        useState<CalculatedComponentActual[]>();

    const [selectedSolvent, setSelectedSolvent] =
        useState<CalculatedComponentActual>();

    const [updatedLots, setUpdatedLots] =
        useState<any[]>([]);

    const [rawMaterialSearchDialogVisible, setRawMaterialSearchDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.batchId]);

    const lotsEditorRender = (cell) => {
        const onValueChanged = (e: SelectBoxTypes.ValueChangedEvent) => {
            //console.log("args:");
            //console.log(e);
            //console.log("cell:");
            //console.log(cell);

            if (updatedLots && cell.data) {
                const matchingUpdatedLot = updatedLots.find(l => l.componentActualId === cell.data.componentActualId);

                if (!matchingUpdatedLot) {
                    let editedUpdatedLots = [...updatedLots];
                    editedUpdatedLots.push({ componentActualId: cell.data.componentActualId, lot: e.value });

                    setUpdatedLots(editedUpdatedLots);
                }
            }

            cell.setValue(e.value && e.value.batchName ? e.value.batchName : undefined);
        };

        const itemRender = (data) => {
            if (data) {
                const lot = data;

                return (
                    <div id={`asset-${lot.batchName}`}>
                        <div className="item-main">{lot.batchName}</div>
                    </div>
                );
            }
            else
                return <div>NA</div>;
        }

        return (
            <SelectBox dataSource={cell.data && cell.data.rawMaterialLots ? cell.data.rawMaterialLots : undefined}
                defaultValue={cell.value}
                {...cell.column.lookup}
                onValueChanged={onValueChanged}
                itemRender={itemRender} />
        );
    }


    const assetEditorRender = (cell) => {
        const onValueChanged = (e: SelectBoxTypes.ValueChangedEvent) => cell.setValue(e.value);

        const itemRender = (data) => {
            if (data) {
                const asset = data;

                return (
                    <div id={`asset-${asset.assetNumber}`}>
                        <div className="item-main">{asset.assetNumber}</div>
                        <div className="item-sub">{asset.description}</div>
                    </div>
                );
            }
            else
                return <div>NA</div>;
        }

        return (
            <SelectBox
                defaultValue={cell.value}
                {...cell.column.lookup}
                onValueChanged={onValueChanged}
                itemRender={itemRender} />
        );
    }

    const user = UserDataStore.getCurrentUser();
    //const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    //const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canInsertRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canDeleteRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    {/*
                    <Button id="resyncToProductPlanningButton" text="Resync from Planning" icon="save" type="success"
                        className="pw-button"
                        hint="add any missing solvents from product planning"
                        height={'1.8rem'}
                        onClick={onResyncToProductPlanningButtonClick} />
                        */}

                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            <div>
                <DataGrid id="ProductionBenchSheetSolventsDataGrid"
                    ref={datagridRef} height="16rem"
                    wordWrapEnabled={true}
                    dataSource={solvents ? solvents : []}
                    onRowValidating={onSolventValidating}
                    rowAlternationEnabled={true}
                    showBorders={true}
                    allowColumnResizing={true}
                    onSelectionChanged={onSelectionChanged}
                    onRowRemoving={onSolventRowRemoving}
                    onRowUpdated={onSolventRowUpdated}
                    showRowLines={true}
                    columnResizingMode='nextColumn'>

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <Paging enabled={false} />

                    <Column name="bc0" type="buttons" width='5rem' cssClass="table-vertical-align">
                        {canDelete &&
                            <ColumnButton hint="delete" icon="trash"
                                onClick={onSolventRowRemoving} />
                        }
                        <ColumnButton hint="audit information" icon="clock" />
                        <ColumnButton hint="information" icon="info" />
                    </Column>

                    <Column width='2rem' cssClass="table-vertical-align"
                        cellRender={editMarkerCellRender} />

                    <Column dataField='solventRatio' cssClass="table-vertical-align"
                        dataType="number" allowEditing={true}
                        width='5rem' alignment='center' />

                    <Column dataField={'rawMaterialName'} cssClass="table-vertical-align"
                        allowEditing={false} width='12rem' />

                    {/*  cellRender={solventRawMaterialRender} */}

                    <Column name="bc1" type="buttons" width={"2rem"} cssClass="table-vertical-align">
                        <ColumnButton hint="raw material lookup" icon="find"
                            onClick={onLookupRawMaterialClick} />
                    </Column>

                    <Column dataField={'rawMaterialBatchName'} caption="Lot ID" width='8rem'
                        cellRender={solventLotFieldRender}
                        editCellRender={lotsEditorRender}
                        cssClass="table-vertical-align" />

                    <Column dataField={'purity'} dataType="number" allowEditing={false}
                        cssClass="table-vertical-align"
                        format={decimalFormat} width='6rem' />

                    <Column dataField={'balance'} width='8rem'
                        cellRender={assetItemRender}
                        editCellRender={assetEditorRender}
                        cssClass="table-vertical-align" >
                        <Lookup dataSource={props.balances ? props.balances : []}
                            allowClearing={true}
                            displayExpr='assetNumber' />
                    </Column>

                    <Column dataField={'weight'} caption="Weight(mg)"
                        cssClass="table-vertical-align" allowEditing={false}
                        dataType="number" format={decimalFormat} width='6rem' />

                    <Column dataField={'rawMaterialBatchExhausted'} dataType="boolean" caption="Exhausted"
                        width='5rem' cssClass="table-vertical-align" />

                    <Column />

                    <Summary>
                        <TotalItem column="weight" summaryType="sum" valueFormat={Decimal4Format} />
                    </Summary>

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Matrix</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button width='1.9rem' type='normal' icon="repeat"
                                hint="duplicate rows"
                                onClick={duplicateSolventRow} />
                        </ToolbarItem>

                        {canInsert &&
                            <ToolbarItem location='after'>
                                <Button width='1.9rem' type='normal' icon="plus"
                                    hint="add component actual"
                                    onClick={onAddSolventEntry} />
                            </ToolbarItem>
                        }
                    </Toolbar>

                    <Editing mode="cell"
                        useIcons={false}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog key={`ibi${key}`} initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog} />
                </DialogPopup>
            }

            {rawMaterialSearchDialogVisible &&
                <DialogPopup visible={rawMaterialSearchDialogVisible}
                    onHiding={hideRawMaterialSearchDialog}
                    title="Raw Material Search"
                    width={'55em'}
                    height={'40em'}>
                    <RawMaterialSearch
                        onCancel={hideRawMaterialSearchDialog}
                        onApplySelection={onApplyRawMaterialSelection} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );
        
    function onLookupRawMaterialClick(e: any) {
        console.log("onLookupRawMaterialClick");
        console.log(e);

        if (e.row && e.row.data)
            setSelectedSolvent(e.row.data);

        showRawMaterialSearchDialog();
    }

    async function onApplyRawMaterialSelection(rawMaterial: RawMaterial) {
        console.log("onApplyRawMaterialSelection");
        console.log(rawMaterial);

        console.log(selectedSolvent);

        //get lots with RM
        let rawMaterialLots: RawMaterialBatch[] = [];

        if (rawMaterial && rawMaterial.rawMaterialId) {
            await getRawMaterialBatches(rawMaterial.rawMaterialId)
                .then(data => {
                    rawMaterialLots = data;
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Raw Material Batches");
                });
                        }

        if (selectedSolvent) {
            let editedSolvents = [...solvents];

            let matchingSolvent = editedSolvents.find(s => s.componentActualId === selectedSolvent.componentActualId);

            console.log(241);

            /* REWORK
            if (matchingSolvent) {
                matchingSolvent.rawMaterialId = rawMaterial.rawMaterialId;
                matchingSolvent.rawMaterialName = rawMaterial.name;
                matchingSolvent.rawMaterialDescription = rawMaterial.description;
                matchingSolvent.rawMaterialLots = rawMaterialLots;

                if (matchingSolvent.editType !== EditType.Insert)
                    matchingSolvent.editType = EditType.Update;

                setSolvents(editedSolvents);
            }
            */
        }

        setRawMaterialSearchDialogVisible(false);
    }

    function onSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedSolvent(e.selectedRowsData[0]);
        }
    }

    async function onSaveClick() {
        saveSolventsData();
    }

    function onCloseClick() {
        const anyNonSavedChanges = solvents.some(s => s.editType !== EditType.None);

        /* REWORK
        if (anyNonSavedChanges) {
            const modalContext: XXXContext = {
                modalType: ConfirmationModal,
                title: "Unsaved Items",
                message: "You have unsaved changes. Do you want to close anyway?",
                onModalConfirmed: closeDialog
            };

            XXX.publish(ModalSubscriberTopic, modalContext);
            return;
        }
        */

        closeDialog();
    }

    async function closeDialog() {
        if (props.onClose)
            props.onClose();
    }

    function onAddSolventEntry(e: any) {
        if (!canInsert)
            return;

        let newSolvent: CalculatedComponentActual = {
            componentActualId: new Guid().toString(),
            elution: 0,
            editType: EditType.Insert
        };

        let editedSolvents = [...solvents];

        editedSolvents.push(newSolvent);

        editedSolvents.sort((a, b) => a.elution - b.elution);

        setSolvents(editedSolvents);
    }

    function duplicateSolventRow() {
        if (selectedSolvent) {
            let editedSolvents = [...solvents];
            let newSolvent = { ...selectedSolvent };

            newSolvent.editType = EditType.Insert;
            newSolvent.componentActualId = new Guid().toString();

            editedSolvents.push(newSolvent);

            editedSolvents.sort((a, b) => a.elution - b.elution);

            setSolvents(editedSolvents);
        }
    }

    function onSolventRowRemoving(e: any) {
        if (!canDelete)
            return;

        console.log("onSolventRowRemoving");
        console.log(e);

        if (e.row && e.row.data && e.row.data.componentActualId) {
            let editedSolvents = [...solvents];

            let matchingSolvent = editedSolvents.find(s => s.componentActualId === e.row.data.componentActualId);

            if (matchingSolvent) {
                //if element was just added but not saved, just get rid of it
                if (matchingSolvent.editType === EditType.Insert) {
                    const idx = editedSolvents.findIndex(ca => ca.componentActualId === e.row.data.componentActualId);

                    if (typeof idx === 'number')
                        editedSolvents.splice(idx, 1);
                }
                else {
                    matchingSolvent.editType = EditType.Delete;
                }

                setSolvents(editedSolvents);
            }
        }
    }

    function onSolventValidating(e: any) {
        console.log("onSolventValidating--");
        console.log(e);

        if (e.oldData && e.newData) {
            if (e.oldData.editType !== EditType.Insert)
                e.newData.editType = EditType.Update;

            if (e.newData.hasOwnProperty('rawMaterialBatchExhausted')) {

                if (e.oldData.rawMaterialLots) {
                    let matchingLot = e.oldData.rawMaterialLots.find(l => l.batchName === e.oldData.rawMaterialBatchName);

                    console.log("Matching Lot?");
                    console.log(matchingLot);

                    if (matchingLot)
                        matchingLot.exhausted = e.newData.rawMaterialBatchExhausted;

                    console.log('OK then');
                }
            }
        }
    }

    function onSolventRowUpdated(e: any) {
        console.log("onSolventRowUpdated");
        console.log(e);

        //this is needed, because tabbing out of a cell does not fire the renderer (for weight colorization)
        if (datagridRef && datagridRef.current && datagridRef.current.instance) {

            console.log("repainting--------------------------------------------------------------------------...");
            datagridRef.current.instance.refresh();
        }
    }

    async function saveSolventsData() {
        if (!solvents)
            return;

        if (datagridRef && datagridRef.current && datagridRef.current.instance) {
            await datagridRef.current.instance.saveEditData()
                .then(async () => {
                    let preparedSolvents = [...solvents];

                    console.log("preparedSolvents... BEGIN");
                    console.log(preparedSolvents);

                    preparedSolvents.forEach(s => {
                        s.batchId = props.batchId;

                        if (updatedLots) {
                            const matchingUpdatedLot = updatedLots.find(l => l.componentActualId === s.componentActualId);

                            if (matchingUpdatedLot && matchingUpdatedLot.lot) {
                                console.log("matchingUpdatedLot:");
                                console.log(matchingUpdatedLot);

                                //REWORK
                                //s.rawMaterialLotId = matchingUpdatedLot.lot.rawMaterialBatchId;
                            }
                        }
                    });

                    console.log("preparedSolvents...AFTER");
                    console.log(preparedSolvents);

                    console.log("updatedLots...");
                    console.log(updatedLots);

                    await updateSolventsData(preparedSolvents)
                        .then((data) => {
                            getSolvents();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Updating Solvents");
                        });
                })
        }
    }

    async function getSolvents() {
        await CrudFunctions.getData(`products/solventsbybatchid/${props.batchId}`)
            .then(data => {
                setSolvents(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Solvents");
            });
    }

    async function getRawMaterialBatches(rawMaterialId: string) {
        return await CrudFunctions.getData(`products/rawmaterialbatchesbyrawmaterialid/${rawMaterialId}`);
    }

    async function updateSolventsData(preparedSolvents: CalculatedComponentActual[]) {
        await CrudFunctions.putData('products/productionbenchsheetsolventsdata', preparedSolvents)
            .then(data => {
                setSolvents(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Solvents");
            });
    }

    async function getAllData() {
        await getSolvents();
    }

    //show/hide

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showRawMaterialSearchDialog() {
        setRawMaterialSearchDialogVisible(true);
    }

    function hideRawMaterialSearchDialog() {
        setRawMaterialSearchDialogVisible(false);
    }

    //render

    function solventRawMaterialRender(e: any) {
        if (e.data && e.data.rawMaterialName) {
            return (
                <div>
                    <div className="item-main">{e.data.rawMaterialName}</div>
                    <div className="item-sub">{e.data.rawMaterialDescription}</div>
                </div>
            );
        }
        else
            return (<div></div>);
    }

    function solventLotFieldRender(e: any) {
        if (e.data && e.data.rawMaterialBatchName) {
            if (e.data.latestUpdate) {
                if (PWDates.isPreviousDateMoreRecentThanDays(e.data.latestUpdate, 7)) {
                    return (<div className="date-expired">{e.data.rawMaterialBatchName}</div>);

                }
                else if (PWDates.isPreviousDateMoreRecentThanDays(e.data.latestUpdate, 30)) {
                    return (<div className="date-warning">{e.data.rawMaterialBatchName}</div>);
                }
                else
                    return (<div>{e.data.rawMaterialBatchName}</div>);
            }
            else
                return (<div>{e.data.rawMaterialBatchName}</div>);
        }
        else {
            return (<div></div>);
        }
    }

    function assetItemRender(e: any) {
        //console.log("assetItemRender");
        //console.log(e);

        if (e.data) {
            if (e.data.balance) {
                const balance = e.data.balance;

                if (balance) {
                    return (
                        <div id={`asset-${balance.assetNumber ? balance.assetNumber : 'NA'}`}>
                            <div className="item-main">{balance.assetNumber ? balance.assetNumber : 'NA'}</div>
                            <div className="item-sub">{balance.description ? balance.description : 'NA'}</div>
                        </div>
                    );
                }
                else
                    return (<div></div>);
            }
            else
                return (<div></div>);

        }
    }


    function solventLotFieldEditRender(e: any) {
        console.log("solventLotFieldEditRender");
        console.log(e);
        //e.data.rawMaterialLots

        if (!e.data || !e.data.rawMaterialLots)
            return (<div></div>);

        let onValueChanged = onSolventBatchIdChanged.bind(this, e);

        return <div>
            <SelectBox className="selectbox"
                defaultValue={e.value}
                placeholder='lot #'
                valueExpr="rawMaterialBatchId"
                displayExpr="batchName"
                showClearButton={true}
                onValueChanged={onValueChanged}
                dataSource={e.data && e.data.rawMaterialLots ? e.data.rawMaterialLots : []} />
        </div>
    }

    function onSolventBatchIdChanged(cell: any, e: any) {
        cell.setValue(e.value);
    }

    function editMarkerCellRender(e: any) {
        if (e.data) {
            switch (e.data.editType) {
                case EditType.Insert:
                    return <div><i className="dx-icon-add" /></div>;
                case EditType.Update:
                    return <div><i className="dx-icon-edit" /></div>;
                case EditType.Delete:
                    return <div><i className="dx-icon-trash" /></div>;
                default:
                    return <div></div>;
            }
        }
        return <div></div>;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}